<template>
    <page :tabs="tabs" :busy="busy">
        <grid ref="grid" :columnDefs="columnDefs" :rowData="rowData" :pinnedBottomRowData="pinnedBottomRowData">
            <date-range-picker
                :range-options="predefinedRanges"
                :value="dateRange"
                :date-format="userUnicodeDateFormat"
                @input="handleDaterangeChange"
            ></date-range-picker>
            <responsive-button
                :href="exportExcelHref"
                @click="generatingExcelReportSuccessToast"
                :icon="['fas', 'file-excel']"
                variant="primary"
            ></responsive-button>
            <exportToPDFButton :href="exportPdfHref" :pdfCacheKey="pdfCacheKey" />
        </grid>
    </page>
</template>

<script>
import API from '../../../api';
import grid from '../../generic/grid/grid.vue';
import page from '../../generic/page/page.vue';
import format from 'date-fns/format';
import { ADMIN_ISSUED_POLICIES_LIST_ENDPOINT } from '../../../endpoints';
import DateRangePicker, {
    ALL_DATES,
    LAST_MONTH,
    LAST_YEAR,
    nameToRange,
    THIS_MONTH,
    THIS_YEAR,
    TODAY,
    YESTERDAY,
} from '../../generic/date-range-picker/date-range-picker.vue';
import { mapGetters } from 'vuex';
import ResponsiveButton from '../../generic/responsive-button/responsive-button.vue';
import { defineAsyncComponent } from 'vue';

const exportToPDFButton = defineAsyncComponent(() =>
    import(
        /* webpackChunkName: "lx-export-to-pdf-button" */
        '../../generic/export-to-pdf-button/export-to-pdf-button.vue'
    )
);

export default {
    components: {
        page,
        grid,
        DateRangePicker,
        ResponsiveButton,
        exportToPDFButton,
    },
    data() {
        return {
            busy: null,
            dateRange: nameToRange(THIS_MONTH),
            dateRequestFormat: 'yyyy-MM-dd',
            end_date: null,
            predefinedRanges: [TODAY, YESTERDAY, THIS_MONTH, LAST_MONTH, THIS_YEAR, LAST_YEAR, ALL_DATES],
            rowData: [],
            start_date: null,
            tabs: [
                {
                    title: 'Issued Policies',
                    route: '/app/issued-policies/list/',
                    active: true,
                },
            ],
            total: null,
            pdfCacheKey: null,
        };
    },
    computed: {
        ...mapGetters('header', ['userUnicodeDateFormat', 'userCurrencySymbol']),
        columnDefs() {
            return [
                {
                    field: 'id',
                    headerName: '#',
                    maxWidth: 80,
                    minWidth: 80,
                },
                {
                    field: 'created',
                    headerName: 'Created',
                    minWidth: 140,
                    type: 'dateColumn',
                },
                {
                    field: 'tracking',
                    headerName: 'Tracking',
                    minWidth: 170,
                    valueGetter: ({ data, node }) => {
                        return node.rowPinned ? '' : data.tracking || 'None';
                    },
                },
                {
                    field: 'meta_res.state',
                    headerName: 'I.Status',
                    minWidth: 140,
                    maxWidth: 140,
                },
                {
                    field: 'meta_res.id',
                    headerName: 'Invoice #',
                },
                {
                    field: 'meta_res.ownership',
                    headerName: 'Subscriber',
                    minWidth: 230,
                },
                {
                    field: 'meta_res.user',
                    headerName: 'Policy Holder',
                    minWidth: 190,
                },
                {
                    field: 'insurance_id',
                    headerName: 'Policy ID',
                },
                {
                    field: 'meta_res.from_date',
                    headerName: 'Check In',
                    minWidth: 140,
                    type: 'dateColumn',
                },
                {
                    field: 'meta_res.to_date',
                    headerName: 'Check Out',
                    minWidth: 140,
                    type: 'dateColumn',
                },
                {
                    field: 'cost',
                    headerName: 'Cost (USD)',
                    type: 'amountColumn',
                    maxWidth: 140,
                    minWidth: 140,
                },
            ];
        },

        endDateRequest() {
            return format(this.dateRange.to, this.dateRequestFormat);
        },

        exportExcelHref() {
            return `/app/issued-policies/admin-issued-policies/excel/?start_date=${this.startDateRequest}&end_date=${this.endDateRequest}`;
        },
        exportPdfHref() {
            return `/app/issued-policies/admin-issued-policies/pdf/?start_date=${this.startDateRequest}&end_date=${this.endDateRequest}`;
        },
        startDateRequest() {
            return format(this.dateRange.from, this.dateRequestFormat);
        },
        pinnedBottomRowData() {
            return [
                {
                    cost: this.total?.total_sum ?? 0,
                },
            ];
        },
    },
    mounted() {
        this.fetchData();
    },
    methods: {
        async fetchData() {
            const { data } = await API.get(ADMIN_ISSUED_POLICIES_LIST_ENDPOINT(), {
                params: {
                    start_date: this.startDateRequest,
                    end_date: this.endDateRequest,
                },
            });
            this.pdfCacheKey = data.pdf_cache_key;
            this.rowData = data.policies;
            this.total = data.total;
            this.$refs.grid.gridOptions.context = { total: this.total };
        },
        handleDaterangeChange(value) {
            this.dateRange = { ...value };
            this.fetchData();
        },
    },
};
</script>
